import { useEffect, useState } from "react";
import IMasterClass from "../../../types/MasterClass";
import IPaciente from "../../../types/Paciente";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Snackbar, Switch, TextField, Typography } from "@mui/material";
import IServiceClassInvoice, { IServiceClassInvoiceErrors } from "../../../types/ServiceClassInvoice";
import ConfigUtils from "../../../utils/Config";
import { getCurrencySymbol } from "../../../utils/Factura";
import IServicio from "../../../types/Servicio";
import { useNavigate } from "react-router-dom";
import * as constantes from "../../../common/Constantes";
import IAlert from "../../../types/Alert";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import FacturacionService from "../../../services/FacturacionService";

interface ConfigurarPrecioClaseProps {
  open: boolean;
  onClose: () => void;
  paciente: IPaciente;
  masterClass: IMasterClass;
  onSave: (nuevaConfig: IServiceClassInvoice) => void;
  config?: IServiceClassInvoice | null;
}

const ConfigurarPrecioClase = ({ open, onClose, paciente, masterClass, onSave, config }: ConfigurarPrecioClaseProps) => {
  const simboloMoneda = getCurrencySymbol(ConfigUtils.getUserCurrency());
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });
  const [loading, setLoading] = useState(false);
  const [selectedServicio, setSelectedServicio] = useState<IServicio | undefined>(undefined);
  const [facturable, setFacturable] = useState(true);
  const [motivoNoFacturable, setMotivoNoFacturable] = useState<string>("");
  const [nombre, setNombre] = useState("");
  const [concepto, setConcepto] = useState("");
  const [precio, setPrecio] = useState<number>(0);
  const [precioTotal, setPrecioTotal] = useState(0);
  const [inputPrecioBase, setInputPrecioBase] = useState(selectedServicio?.precio ? selectedServicio.precio.toString().replace(".", ",") : "0,00");
  const [impuesto, setImpuesto] = useState<number>(0);
  const [confirmNoFacturable, setConfirmNoFacturable] = useState(false);
  const [frecuencia, setFrecuencia] = useState<'mensual'>("mensual");

  const [errors, setErrors] = useState<IServiceClassInvoiceErrors>({});

  const handleChangeServicio = (event: SelectChangeEvent) => {
    const serv = servicios.find((s) => s.internalID?.toString() === event.target.value);
    if (serv) {
      setSelectedServicio(serv);
      setFacturable(serv.facturable ?? true);
      setNombre(serv.nombre ?? "");
      setConcepto(serv.concepto ?? "");
      setPrecio(serv.precio ?? 0);
      setImpuesto(serv.impuesto ?? 0);
      const precioTotalCalculado = serv.precio + (serv.precio * serv.impuesto) / 100;
      setPrecioTotal(precioTotalCalculado);
      setInputPrecioBase(serv.precio.toFixed(2).replace(".", ","));
      setFrecuencia(serv.frecuencia as "mensual");
      setErrors({});
    }
  };

  const handleSave = async () => {
    const validationErrors = validateConfig();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const baseConfig = {
      userId: ConfigUtils.getUserId(),
      pacienteId: paciente._id,
      actividadId: Number(masterClass.title),
      frecuencia,
      activo: true,
    };

    let nuevaConfig: IServiceClassInvoice;
    if (!facturable) {
        nuevaConfig = {
          ...baseConfig,
          nombre: selectedServicio?.nombre!,
          concepto: "",
          precio: 0,
          impuesto: 0,
          facturable,
          motivoNoFacturable
        };
    } else {
        nuevaConfig = {
          ...baseConfig,
          nombre: selectedServicio?.nombre! || "",
          concepto: concepto || selectedServicio?.concepto || "",
          precio: facturable ? precio : 0,
          impuesto: facturable ? impuesto : 0,
          facturable,
          activo: true
        };
    }

    try {
      setLoading(true);
      const response = await FacturacionService.saveFacturacionClase(nuevaConfig);
      onSave(response.data);
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "ConfigurarPrecioClase.Component.handleSave",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const validateConfig = () => {
    const errors: IServiceClassInvoiceErrors = {};
    if (!selectedServicio) errors.servicio = "Debes seleccionar un servicio";
    if (!nombre.trim()) errors.servicioName = "El nombre del servicio es obligatorio";
    if (facturable && !concepto.trim()) errors.concepto = "El concepto es obligatorio";
    if (facturable && precio <= 0) errors.precio = "El precio debe ser mayor que 0 si es facturable";
    return errors;
  };

  useEffect(() => {
    if (!open) return;

    if (config) {
      const selectedServ = servicios.find(s => s.nombre === config.nombre);
      setSelectedServicio(selectedServ);

      setFacturable(config.facturable);
      setNombre(config.nombre);
      setConcepto(config.concepto);
      setPrecio(config.precio);
      setImpuesto(config.impuesto);
      setPrecioTotal(config.precio + (config.precio * config.impuesto) / 100);
      setInputPrecioBase(config.precio.toFixed(2).replace(".", ","));
      setFrecuencia(config.frecuencia as "mensual");
      setMotivoNoFacturable(config.motivoNoFacturable || "");
    }

    setErrors({});
  }, [open, config]);

  const actividades = ConfigUtils.getActividades();
  const servicios = ConfigUtils.getServicios();

  return (<>
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>
        {config ? "Editar facturación" : "Configurar facturación"} de {paciente.nombre} {paciente.apellidos}
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {masterClass && actividades.find(a => a.internalID?.toString() === masterClass.title)?.title} – <b>{config && config.clasesSemanales} clase{config && config.clasesSemanales! > 1 ? "s" : ""} semanal{config && config.clasesSemanales! > 1 ? "es" : ""}</b>
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2 }} error={!!errors.servicio}>
          <InputLabel id="select-servicio-label">Servicio*</InputLabel>
          <Select
            labelId="select-servicio-label"
            value={selectedServicio?.internalID?.toString() || ""}
            label="Servicio*"
            onChange={handleChangeServicio}
          >
            {servicios.filter((m) => m.activo && m.recurrente).map((m) => (
              <MenuItem key={m.internalID?.toString()} value={m.internalID?.toString()}>
                {m.nombre}
              </MenuItem>
            ))}
          </Select>
          {errors.servicio && (
            <Typography sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 1 }}>{errors.servicio}</Typography>
          )}
        </FormControl>

        {selectedServicio && <>
            {/* Facturable switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={facturable}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (!value && facturable) {
                      setConfirmNoFacturable(true);
                      return;
                    }
                    setFacturable(value);
                  }}
                />
              }
              label="Facturable"
              sx={{ mb: 2 }}
            />
        </>}

        {facturable && selectedServicio && (<>
        
        {/* Servicio */}
        <TextField
          label="Servicio*"
          fullWidth
          sx={{ mb: 2 }}
          value={nombre}
          onChange={(e) => {
            setNombre(e.target.value);
            setErrors((prev: IServiceClassInvoiceErrors) => ({ ...prev, servicioName: undefined }));
          }}
          error={!!errors.servicioName}
          helperText={errors.servicioName}
        />
        {/* Concepto */}
        <TextField
          label="Concepto*"
          fullWidth
          sx={{ mb: 2 }}
          value={concepto}
          onChange={(e) => {
            setConcepto(e.target.value);
            setErrors((prev: IServiceClassInvoiceErrors) => ({ ...prev, concepto: undefined }));
          }}
          error={!!errors.concepto}
          helperText={errors.concepto}
        />
        {/* Precio */}
        <TextField
          label={`Precio base (${simboloMoneda})*`}
          fullWidth
          type="text"
          sx={{ mb: 2 }}
          value={inputPrecioBase}
          onChange={(e) => {
            let raw = e.target.value;
            setInputPrecioBase(raw);
                              
            const normalized = raw.replace(",", ".");
            if (/^\d*([.,]\d*)?$/.test(raw)) {
              const nuevoPrecio = parseFloat(normalized);
              if (!isNaN(nuevoPrecio)) {
                setPrecio(nuevoPrecio);
                setPrecioTotal(nuevoPrecio + (nuevoPrecio * impuesto) / 100);
              } else {
                setPrecio(0);
                setPrecioTotal(0);
              }
            }
          }}
          onBlur={() => {
            const formatted = precio.toFixed(2).replace(".", ",");
            setInputPrecioBase(formatted);
          }}
          error={!!errors.precio}
          helperText={errors.precio}
        />
        {/* Impuesto */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="select-impuesto-label">Impuesto (%)*</InputLabel>
          <Select
            labelId="select-impuesto-label"
            value={impuesto}
            label="Impuesto (%)*"
            onChange={(e) => {
              const nuevoImp = Number(e.target.value);
              setImpuesto(nuevoImp);
              setPrecioTotal(precio + (precio * nuevoImp) / 100);
            }}
          >
            {constantes.IMPUESTOS.map((imp) => (
              <MenuItem key={imp} value={imp}>
                {imp}%
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Precio total */}
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label={`Precio total (${simboloMoneda})`}
          value={precioTotal.toFixed(2).replace(".", ",")}
          InputProps={{ readOnly: true, sx: {
            backgroundColor: '#f0f0f0',
            color: '#555',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#bdbdbd'
            }
          }}}
        />
        {/* Frecuencia */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Frecuencia*"
            select
            value={frecuencia}
            onChange={(e) => setFrecuencia(e.target.value as "mensual")}
          >
            <MenuItem value="mensual">Mensual</MenuItem>
          </TextField>
        </Grid>
        </>)}

        {!facturable && (
          <TextField
            label="Motivo no facturable"
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={motivoNoFacturable}
            onChange={(e) => {
              setMotivoNoFacturable(e.target.value);
            }}
          />  
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>{config ? "Editar" : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
    
    {/* CONFIRMAR NO FACTURABLE */}
    <Dialog open={confirmNoFacturable} maxWidth="sm" onClose={loading ? undefined : () => setConfirmNoFacturable(false)}>
      <DialogContent>
        <Typography>
          ¿Seguro que quieres marcar a este paciente en esta clase como <b>NO facturable</b>? No aparecerá para facturación.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setFacturable(false);
            setConfirmNoFacturable(false);
            setErrors({});
          }}
          disabled={loading}
        >
          Sí, continuar
        </Button>
        <Button onClick={() => setConfirmNoFacturable(false)} disabled={loading}>Cancelar</Button>
      </DialogActions>
    </Dialog>
    
    <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
      <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
        {alert.msg}
      </Alert>
    </Snackbar>
    </>
  )

};

export default ConfigurarPrecioClase;